import { Collection, Database } from '@nozbe/watermelondb';

import {
    PreparedDataOnInvoiceProductAdd,
    PreparedDataOnInvoiceProductUpdate,
    ProductInvoiceModel,
    ProductInvoicePayload,
} from '../../types/Products';
import { InvoicingProductDBServiceOptions } from '../../types/dbService';
import { DatabaseValidation } from '../../types/validation';
import Organisation from './Organisation';
import { addRelatedDataBatch } from './helpers/invoicingProduct/common';
import {
    logAddDBActions,
    prepareCreateProduct,
} from './helpers/invoicingProduct/invoicingProductAdd';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logUpdateDBActions,
    prepareUpdateProduct,
} from './helpers/invoicingProduct/invoicingProductUpdate';

class InvoicingProduct {
    private database: Database;
    private collection: Collection<ProductInvoiceModel>;
    private options: InvoicingProductDBServiceOptions;
    private validation: DatabaseValidation;
    private table = 'invoicing_products';

    constructor(options: InvoicingProductDBServiceOptions) {
        this.database = options.database;
        this.options = options;
        this.collection = options.database.collections.get(this.table);
        this.validation = options.validation;
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    async addProduct(data: ProductInvoicePayload) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        if (!organisationID) {
            throw new Error(
                '(InvoicingProduct) Organisation not found for add new record',
            );
        }

        return this.database.write(async () => {
            const product = prepareCreateProduct({
                collection: this.collection,
                payload: data,
                userId: data.userID,
                organisationId: organisationID,
            });

            const preparedData: PreparedDataOnInvoiceProductAdd[] = [
                {
                    record: product,
                    table: 'invoicing_products',
                    operation: 'add',
                },
            ];

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logAddDBActions({
                product,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return product;
        }, 'create-invoicing-product');
    }

    async updateProduct(data: ProductInvoicePayload & { id: string }) {
        return this.database.write(async () => {
            const product = await prepareUpdateProduct({
                collection: this.collection,
                id: data.id,
                payload: data,
            });

            const preparedData: PreparedDataOnInvoiceProductUpdate[] = [
                {
                    record: product,
                    table: 'invoicing_products',
                    operation: 'update',
                },
            ];

            validateEntityRecords(
                preparedData.filter((r) => r.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logUpdateDBActions({
                product,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return product;
        }, 'update-invoicing-product');
    }
}

export default InvoicingProduct;
